import { Checkbox, TextInput, TextInputSizes, Toggle, Tooltip } from '@silverfort/sf-components';
import { useSmartPolicyContext } from '../../SmartPolicyContext';
import { SmartPolicyAction } from '../../SmartPoliciesTypes';
import { useMemo } from 'react';
import { RISK_RULES } from '@silverfort-nodelibs/common/utils/insight';
import { PricingModules, usePricingModulesState } from '../../../../../../../context/contexts/PricingModuleContext';
import useHasPermissions from '../../../../../../../hooks/useHasPermissions';
import { Pages, Roles } from '../../../../../../../context/contexts/UserContext/permissionsTypes';

interface ActionOptionProps {
    label: string;
    onCheck: () => void;
    checked: boolean;
    checkDisabled?: boolean;
    disabledTooltip?: string;
    onDaysChange?: (days: number) => void;
    daysValue?: number;
    minDaysToActivate?: number;
    maxDaysToActivate?: number;
    disabled?: boolean;
}

const ActionOption = ({
    label,
    onCheck: onCheckChange,
    checked,
    checkDisabled,
    disabledTooltip,
    onDaysChange,
    daysValue,
    minDaysToActivate,
    maxDaysToActivate,
    disabled
}: ActionOptionProps) => {
    const errorMsg: string | undefined = useMemo(() => {
        if (minDaysToActivate === undefined || maxDaysToActivate === undefined) {
            return;
        }
        if (daysValue === undefined || daysValue < minDaysToActivate || daysValue > maxDaysToActivate) {
            return `${minDaysToActivate}-${maxDaysToActivate}`;
        }
    }, [daysValue, minDaysToActivate, maxDaysToActivate]);

    return (
        <div className="border border-sf-gray-100 rounded-md p-4 flex flex-col gap-3">
            {checkDisabled && disabledTooltip ? (
                <div>
                    <Tooltip title={disabledTooltip}>
                        <Checkbox
                            label={label}
                            onChange={onCheckChange}
                            checked={checked}
                            disabled={checkDisabled || disabled}
                        />
                    </Tooltip>
                </div>
            ) : (
                <Checkbox
                    label={label}
                    onChange={onCheckChange}
                    checked={checked}
                    disabled={checkDisabled || disabled}
                />
            )}
            {onDaysChange && (
                <div className="flex gap-2 items-center">
                    <div className="text-sf text-sf-gray-500">Activate</div>
                    <div>
                        <TextInput
                            className="[&&]:w-20"
                            inputSize={TextInputSizes.Small}
                            onChange={(value) => !value.includes('.') && onDaysChange(Number(value))}
                            value={daysValue?.toString() || ''}
                            numbersOnly={true}
                            validationMessage={errorMsg}
                            validationProps={{ className: 'absolute mt-1', showIcon: false }}
                            disabled={disabled}
                        />
                    </div>
                    <div className="text-sf text-sf-gray-500">days after last baseline change</div>
                </div>
            )}
        </div>
    );
};

type AdditionalActionToggleProps = {
    isActive: boolean;
    onToggleIsActive: (checked: boolean) => void;
    toggleLabel: string;
    disabled?: boolean;
    tooltipTitle?: string;
};

const AdditionalActionToggle = ({
    isActive,
    onToggleIsActive,
    toggleLabel,
    disabled,
    tooltipTitle
}: AdditionalActionToggleProps) => {
    return (
        <div>
            <Tooltip title={tooltipTitle} disableHoverListener={!tooltipTitle}>
                <Toggle label={toggleLabel} disabled={disabled} checked={isActive} onChange={onToggleIsActive} />
            </Tooltip>
        </div>
    );
};

type ActivitiesActionProps = AdditionalActionToggleProps & {
    daysValue: number;
    onDaysChange: (days: number) => void;
    minDaysToActivate?: number;
    maxDaysToActivate?: number;
};

const ActivitiesAction = ({
    isActive,
    onToggleIsActive,
    toggleLabel,
    daysValue,
    onDaysChange,
    minDaysToActivate,
    maxDaysToActivate,
    disabled,
    tooltipTitle
}: ActivitiesActionProps) => {
    const errorMsg: string | undefined = useMemo(() => {
        if (minDaysToActivate === undefined || maxDaysToActivate === undefined) {
            return;
        }
        if (daysValue === undefined || daysValue < minDaysToActivate || daysValue > maxDaysToActivate) {
            return `${minDaysToActivate}-${maxDaysToActivate}`;
        }
    }, [daysValue, minDaysToActivate, maxDaysToActivate]);
    return (
        <div className="flex flex-col gap-2">
            <div>
                <AdditionalActionToggle
                    isActive={isActive}
                    onToggleIsActive={onToggleIsActive}
                    toggleLabel={toggleLabel}
                    disabled={disabled}
                    tooltipTitle={tooltipTitle}
                />
            </div>
            <div className="pl-8 flex gap-2 items-center">
                <div className="text-sf text-sf-gray-500">active in the last</div>
                <div>
                    <TextInput
                        className="[&&]:w-20"
                        inputSize={TextInputSizes.Small}
                        onChange={(value) => !value.includes('.') && onDaysChange(Number(value))}
                        value={daysValue?.toString() || ''}
                        numbersOnly={true}
                        validationMessage={errorMsg}
                        validationProps={{ className: 'absolute mt-1', showIcon: false }}
                        disabled={disabled}
                    />
                </div>
                <div className="text-sf text-sf-gray-500">days</div>
            </div>
        </div>
    );
};

const ActionsTab = () => {
    const { smartPolicyForEdit, setSmartPolicyForEdit, smartPoliciesConfig } = useSmartPolicyContext();
    const { pricingModulesState } = usePricingModulesState();
    const isItdrOrIspmEnabled =
        pricingModulesState?.[PricingModules.ITDR].onPremEnabled ||
        pricingModulesState?.[PricingModules.ISPM].onPremEnabled;
    const isEditor = useHasPermissions({ permission: Pages.SERVICE_ACCOUNTS, role: Roles.EDITOR });
    if (!smartPolicyForEdit || !smartPoliciesConfig) return <></>;

    const isActionActive = (action: SmartPolicyAction) => {
        return smartPolicyForEdit.activated_action.some((a) => a === action);
    };
    const removeOrAddAction = (action: SmartPolicyAction) => {
        if (isActionActive(action)) {
            const newActiveActions = smartPolicyForEdit.activated_action.filter((a) => a !== action);
            setSmartPolicyForEdit({
                ...smartPolicyForEdit,
                activated_action: newActiveActions,
                send_to_siem: newActiveActions.length > 0 ? smartPolicyForEdit.send_to_siem : false,
                protect_risk_indicators: newActiveActions.length > 0 ? smartPolicyForEdit.protect_risk_indicators : [],
                is_limit_activities: newActiveActions.length > 0 ? smartPolicyForEdit.is_limit_activities : false
            });
        } else {
            setSmartPolicyForEdit({
                ...smartPolicyForEdit,
                activated_action: [...smartPolicyForEdit.activated_action, action]
            });
        }
    };

    const isInteractiveLogin = smartPolicyForEdit.protect_risk_indicators.includes(
        RISK_RULES.SERVICE_ACCOUNT_INTERACTIVE_LOGIN
    );

    const toggleInteractiveLogin = () => {
        setSmartPolicyForEdit({
            ...smartPolicyForEdit,
            protect_risk_indicators: isInteractiveLogin
                ? smartPolicyForEdit.protect_risk_indicators.filter(
                      (rule) => rule !== RISK_RULES.SERVICE_ACCOUNT_INTERACTIVE_LOGIN
                  )
                : [...smartPolicyForEdit.protect_risk_indicators, RISK_RULES.SERVICE_ACCOUNT_INTERACTIVE_LOGIN]
        });
    };

    const isAdditionalActionEnabled = smartPolicyForEdit.activated_action.length > 0;
    const additionalActionsDisabledTooltip = !isAdditionalActionEnabled
        ? 'To activate, select notify policy, deny policy, or both.'
        : undefined;

    return (
        <div className="flex flex-col ">
            <div className="py-6 flex flex-col gap-6 w-full">
                <ActionOption
                    label="Notify Policy"
                    onCheck={() => removeOrAddAction(SmartPolicyAction.ALTER_AFTER)}
                    checked={isActionActive(SmartPolicyAction.ALTER_AFTER)}
                    onDaysChange={(days) => {
                        setSmartPolicyForEdit({
                            ...smartPolicyForEdit,
                            alter_after: days
                        });
                    }}
                    daysValue={smartPolicyForEdit.alter_after}
                    minDaysToActivate={smartPoliciesConfig.minDaysToActivate}
                    maxDaysToActivate={smartPoliciesConfig.maxDaysToActivate}
                    disabled={!isEditor}
                />
                <ActionOption
                    label="Deny Policy"
                    onCheck={() => removeOrAddAction(SmartPolicyAction.BLOCK_AFTER)}
                    checked={isActionActive(SmartPolicyAction.BLOCK_AFTER)}
                    onDaysChange={(days) => {
                        setSmartPolicyForEdit({
                            ...smartPolicyForEdit,
                            block_after: days
                        });
                    }}
                    daysValue={smartPolicyForEdit.block_after}
                    minDaysToActivate={smartPoliciesConfig.minDaysToActivate}
                    maxDaysToActivate={smartPoliciesConfig.maxDaysToActivate}
                    disabled={!isEditor}
                />
            </div>
            <div className="p-6 flex flex-col gap-4 border border-sf-gray-100 rounded-md h-full">
                <AdditionalActionToggle
                    toggleLabel="Send To SIEM"
                    isActive={smartPolicyForEdit.send_to_siem}
                    onToggleIsActive={() => {
                        setSmartPolicyForEdit({
                            ...smartPolicyForEdit,
                            send_to_siem: !smartPolicyForEdit.send_to_siem
                        });
                    }}
                    disabled={!isAdditionalActionEnabled || !isEditor}
                    tooltipTitle={!isAdditionalActionEnabled ? additionalActionsDisabledTooltip : undefined}
                />
                {isItdrOrIspmEnabled && (
                    <AdditionalActionToggle
                        toggleLabel="Limit interactive login"
                        isActive={isInteractiveLogin}
                        onToggleIsActive={toggleInteractiveLogin}
                        disabled={!isAdditionalActionEnabled || !isEditor}
                        tooltipTitle={!isAdditionalActionEnabled ? additionalActionsDisabledTooltip : undefined}
                    />
                )}
                <ActivitiesAction
                    isActive={smartPolicyForEdit.is_limit_activities}
                    onToggleIsActive={() => {
                        setSmartPolicyForEdit({
                            ...smartPolicyForEdit,
                            is_limit_activities: !smartPolicyForEdit.is_limit_activities
                        });
                    }}
                    toggleLabel="Allow sources, destinations, and protocols"
                    daysValue={smartPolicyForEdit.days_limit_activities}
                    onDaysChange={(days) => {
                        setSmartPolicyForEdit({
                            ...smartPolicyForEdit,
                            days_limit_activities: days
                        });
                    }}
                    minDaysToActivate={smartPoliciesConfig.minDaysToLimitActivities}
                    maxDaysToActivate={smartPoliciesConfig.maxDaysToLimitActivities}
                    disabled={!isAdditionalActionEnabled || !isEditor}
                    tooltipTitle={!isAdditionalActionEnabled ? additionalActionsDisabledTooltip : undefined}
                />
            </div>
        </div>
    );
};

export default ActionsTab;
